import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, Outlet, useOutletContext, useLocation } from "react-router-dom";
import {
  Plus, MessageSquare, Search, FileText, BookMarked,
  ShieldCheck, StickyNote, Bell, PanelLeftClose, PanelLeft, Settings as SettingsIcon,
  CreditCard, LogOut, Coins, Sparkles, ClipboardList, FolderKanban, Menu, X
} from "lucide-react";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/app", icon: Sparkles, label: "Tableau de bord", end: true, testid: "dashboard" },
  { to: "/app/chat", icon: MessageSquare, label: "Conversations", testid: "chat" },
  { to: "/app/projects", icon: FolderKanban, label: "Mes projets", testid: "projects" },
  { to: "/app/search", icon: Search, label: "Revue de littérature", testid: "search" },
  { to: "/app/documents", icon: FileText, label: "Documents", testid: "documents" },
  { to: "/app/bibliography", icon: BookMarked, label: "Bibliographie", testid: "bibliography" },
  { to: "/app/similarity", icon: ShieldCheck, label: "Vérification", testid: "similarity" },
  { to: "/app/notes", icon: StickyNote, label: "Notes", testid: "notes" },
  { to: "/app/forms", icon: ClipboardList, label: "Forms / Questionnaires", testid: "forms" },
];

export function useApp() { return useOutletContext(); }

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [credits, setCredits] = useState(user?.credits ?? 0);
  const [unread, setUnread] = useState(0);
  const [plan, setPlan] = useState(user?.plan);

  const refreshMeta = useCallback(async () => {
    try {
      const [b, m] = await Promise.all([api.get("/billing/me"), api.get("/messages")]);
      setCredits(b.data.credits);
      setPlan(b.data.plan?.key);
      setUnread(m.data.unread);
    } catch {}
  }, []);

  useEffect(() => { refreshMeta(); }, [refreshMeta]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const doLogout = async () => { await logout(); navigate("/"); };
  const closeMobile = () => setMobileOpen(false);
  const showLabels = !collapsed || mobileOpen;

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
      isActive ? "bg-lisungi-yellow/15 text-lisungi-yellow font-semibold" : "text-white/70 hover:bg-white/10 hover:text-white"
    }`;

  return (
    <div className="h-screen flex bg-white overflow-hidden">
      {/* Mobile backdrop */}
      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={closeMobile} data-testid="sidebar-backdrop" />}

      {/* SIDEBAR */}
      <aside
        className={`bg-navy flex flex-col transition-transform duration-300 shrink-0
          fixed inset-y-0 left-0 z-50 w-64 lg:static lg:z-auto lg:translate-x-0
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          ${collapsed ? "lg:w-[76px]" : "lg:w-64"}`}
        data-testid="app-sidebar"
      >
        <div className="p-4 flex items-center justify-between">
          {showLabels && <Logo variant="light" />}
          {/* desktop collapse toggle */}
          <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:block text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10" data-testid="sidebar-toggle">
            {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          {/* mobile close */}
          <button onClick={closeMobile} className="lg:hidden text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10" data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-3">
          <Button onClick={() => navigate("/app/projects?new=1")} className="w-full bg-lisungi-yellow text-navy hover:bg-lisungi-yellow/90 font-semibold rounded-lg gap-2" data-testid="new-project-btn">
            <Plus className="h-4 w-4" /> {showLabels && "Nouveau projet"}
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={closeMobile} className={linkClass} data-testid={`nav-${n.testid}`} title={n.label}>
              <n.icon className="h-[18px] w-[18px] shrink-0" />
              {showLabels && <span className="truncate">{n.label}</span>}
            </NavLink>
          ))}
          <NavLink to="/app/messages" onClick={closeMobile} className={linkClass} data-testid="nav-messages" title="Messages">
            <div className="relative"><Bell className="h-[18px] w-[18px] shrink-0" />
              {unread > 0 && <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-lisungi-yellow text-navy text-[10px] font-bold flex items-center justify-center">{unread}</span>}
            </div>
            {showLabels && <span className="flex-1 flex items-center justify-between">Messages {unread > 0 && <span className="text-[10px] text-lisungi-yellow font-mono">{unread} nouveaux</span>}</span>}
          </NavLink>
        </nav>

        {/* FOOTER */}
        <div className="border-t border-white/10 p-3 space-y-1">
          {showLabels && (
            <button onClick={() => navigate("/app/billing")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-left" data-testid="credits-widget">
              <Coins className="h-4 w-4 text-lisungi-yellow" />
              <div className="flex-1"><p className="text-xs text-white/60">Crédits</p><p className="text-sm font-semibold text-white">{credits} disponibles</p></div>
            </button>
          )}
          <div className="flex items-center gap-1">
            <button onClick={() => navigate("/app/billing")} className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-white/70 hover:bg-white/10 text-sm" data-testid="nav-billing"><CreditCard className="h-4 w-4" />{showLabels && "Abonnement"}</button>
          </div>
          <button onClick={() => navigate("/app/settings")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white/70 hover:bg-white/10 text-sm" data-testid="nav-settings"><SettingsIcon className="h-4 w-4" />{showLabels && "Paramètres"}</button>
          <div className="flex items-center gap-2 px-2 pt-2 mt-1 border-t border-white/10">
            <div className="h-8 w-8 rounded-full bg-lisungi-yellow text-navy flex items-center justify-center font-bold text-sm shrink-0">{(user?.name || "U")[0].toUpperCase()}</div>
            {showLabels && (
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{user?.name}</p>
                <p className="text-[10px] text-white/50 truncate font-mono uppercase">{plan}</p>
              </div>
            )}
            <button onClick={doLogout} className="text-white/50 hover:text-lisungi-yellow p-1" title="Déconnexion" data-testid="logout-btn"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>

      {/* MAIN COLUMN */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center gap-3 bg-navy px-4 h-14 shrink-0">
          <button onClick={() => setMobileOpen(true)} className="text-white p-1.5 rounded-lg hover:bg-white/10" data-testid="mobile-menu-btn" aria-label="Ouvrir le menu">
            <Menu className="h-6 w-6" />
          </button>
          <div className="scale-90 origin-left"><Logo variant="light" /></div>
          <button onClick={() => navigate("/app/billing")} className="ml-auto flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5 text-white text-sm" data-testid="mobile-credits">
            <Coins className="h-4 w-4 text-lisungi-yellow" /> {credits}
          </button>
        </div>

        <main className="flex-1 overflow-y-auto bg-lisungi-bg">
          <Outlet context={{ credits, setCredits, refreshMeta, unread, user, plan }} />
        </main>
      </div>
    </div>
  );
}
